import { useAdmin } from '@/concepts/Admin/hooks/useAdmin'
import Partner from '@/concepts/Admin/types/Partner'
import Storage from '@/lib/Storage'
import { capitalizeString } from '@/utils/capitalizeString'
import { isEmpty } from '@/utils/isEmpty'
import { KeyboardArrowDown } from '@mui/icons-material'
import { Box, Button, Menu, MenuItem, Theme, useTheme } from '@mui/material'
import type React from 'react'
import { useState } from 'react'
import { isAdmin } from '../authProvider'
import { usePartnerDetails } from '../concepts/Admin/hooks/usePartnerDetails'
import CONSTANTS from '../constants'

const PUBLISHER_TYPE = CONSTANTS.PARTNER_TYPE_PUBLISHER

const getStyles = (theme: Theme) => ({
  button: {
    color: theme.palette.common.white,
    backgroundColor: 'transparent',
    padding: '12px 16px',
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    minWidth: '200px',
    justifyContent: 'space-between',
    borderRight: `1px solid ${theme.palette.divider}`,
    borderRadius: 0,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    },
    '& .MuiButton-endIcon': {
      marginLeft: 'auto'
    }
  },
  menu: {
    '& .MuiPaper-root': {
      minWidth: '200px',
      backgroundColor: theme.palette.background.paper,
      boxShadow:
        theme.palette.mode === 'dark'
          ? '0 2px 8px rgba(0,0,0,0.5)'
          : '0 2px 8px rgba(0,0,0,0.15)',
      border: `1px solid ${
        theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#e0e0e0'
      }`
    },
    '& .MuiMenuItem-root': {
      padding: '8px 16px',
      fontSize: '14px',
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.palette.action.hover
      },
      '&.Mui-selected': {
        backgroundColor: theme.palette.action.selected,
        '&:hover': {
          backgroundColor: theme.palette.action.selected
        }
      }
    }
  }
})

export const AdminDropdown = () => {
  const theme = useTheme()
  const styles = getStyles(theme)
  const { selectPartner } = useAdmin()
  const { partnerDetails } = usePartnerDetails()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(
    null
  )

  if (!isAdmin()) return null

  if (partnerDetails.length <= 1) return null

  const open = Boolean(anchorEl)

  const activeRole = Storage().getActiveRole()

  if (isEmpty(activeRole)) return null

  const roleType = capitalizeString(activeRole.type)
  const roleName =
    activeRole.type !== PUBLISHER_TYPE ? activeRole.subname : activeRole.name

  const displayValue = `${roleType} - ${roleName}`

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handlePartnerSelect = (partnerId: number) => {
    const selectedPartner = partnerDetails.find(
      (partner) => partner.id === partnerId
    )

    if (selectedPartner) {
      setSelectedPartnerId(partnerId)
      selectPartner(selectedPartner, true)
    }
    handleClose()
  }

  return (
    <Box>
      <Button
        onClick={handleClick}
        endIcon={<KeyboardArrowDown />}
        aria-controls={open ? 'admin-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={styles.button}
      >
        {displayValue}
      </Button>

      <Menu
        id="admin-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'admin-button'
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        sx={styles.menu}
      >
        {partnerDetails.map((partner: Partner) => {
          const displayName =
            partner.type !== 'Publisher' ? partner.company : partner.name
          const partnerType = partner.type
          const label = `${partnerType} - ${displayName}`

          return (
            <MenuItem
              key={partner.id}
              onClick={() => handlePartnerSelect(partner.id)}
              selected={partner.id === selectedPartnerId}
            >
              {label}
            </MenuItem>
          )
        })}
      </Menu>
    </Box>
  )
}
